import React, { useEffect, useState } from "react";
import {
  MapContainer,
  TileLayer,
  useMap,
  Tooltip,
  CircleMarker
} from "react-leaflet";
import L from "leaflet";
import "leaflet.heat";

// Country name to lat/lon map
const countryCoords = {
  "USA": [37.0902, -95.7129],
  "United States": [37.0902, -95.7129],
  "United States of America": [37.0902, -95.7129],
  "Canada": [56.1304, -106.3468],
  "Germany": [51.1657, 10.4515],
  "United Kingdom": [55.3781, -3.4360],
  "UK": [55.3781, -3.4360],
  "France": [46.2276, 2.2137],
  "China": [35.8617, 104.1954],
  "India": [20.5937, 78.9629],
  "Australia": [-25.2744, 133.7751],
  "Japan": [36.2048, 138.2529],
  "Brazil": [-14.2350, -51.9253],
  "South Korea": [35.9078, 127.7669],
  "Italy": [41.8719, 12.5674],
  "Spain": [40.4637, -3.7492],
  "Russia": [61.5240, 105.3188],
  "Netherlands": [52.1326, 5.2913],
};

// HeatLayer component
function HeatLayer({ points }) {
  const map = useMap();

  useEffect(() => {
    if (!points.length) return;

    const heat = L.heatLayer(points, {
      radius: 35,
      blur: 45,
      maxZoom: 4,
      gradient: {
        0.1: "#c6dbef",
        0.3: "#6baed6",
        0.5: "#fdae61",
        0.8: "#f46d43",
        1.0: "#d73027"
      }
    }).addTo(map);

    return () => {
      map.removeLayer(heat);
    };
  }, [map, points]);

  return null;
}

export default function StudyHeatMap() {
  const [heatPoints, setHeatPoints] = useState([]);
  const [showHeat, setShowHeat] = useState(true);

  useEffect(() => {
    fetch("/api/studies")
      .then((res) => res.json())
      .then((data) => {
        const countryCounts = {};

        data.forEach((study) => {
          const raw = study.Country?.trim();
          if (!raw) return;

          const normalized = raw.toLowerCase();
          const match = Object.keys(countryCoords).find(
            (c) =>
              c.toLowerCase() === normalized ||
              normalized.includes(c.toLowerCase()) ||
              c.toLowerCase().includes(normalized)
          );

          if (match) {
            countryCounts[match] = (countryCounts[match] || 0) + 1;
          } else {
            console.warn("Unmatched country:", raw);
          }
        });

        const points = Object.entries(countryCounts)
          .map(([country, count]) => {
            const coords = countryCoords[country];
            return coords ? { coords, count, country } : null;
          })
          .filter(Boolean);

        setHeatPoints(points);
      })
      .catch((err) => console.error("Heat map fetch error:", err));
  }, []);

  return (
    <div
      style={{
        position: "relative",
        height: "500px",
        borderRadius: "12px",
        overflow: "hidden",
        margin: "2rem 0",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)"
      }}
    >
      {/* Toggle Button */}
      <button
        onClick={() => setShowHeat(!showHeat)}
        style={{
          position: "absolute",
          top: "12px",
          right: "12px",
          zIndex: 1000,
          backgroundColor: "#0079C0",
          color: "#fff",
          border: "none",
          padding: "6px 12px",
          borderRadius: "8px",
          cursor: "pointer",
          fontWeight: "bold"
        }}
      >
        {showHeat ? "Hide Heatmap" : "Show Heatmap"}
      </button>

      <MapContainer
        center={[20, 0]}
        zoom={2}
        scrollWheelZoom={false}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          attribution='&copy; OpenStreetMap contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {showHeat && (
          <HeatLayer points={heatPoints.map(p => [...p.coords, p.count * 2])} />
        )}

        {/* Markers with animated pulse and tooltip */}
        {heatPoints.map(({ coords, count, country }, idx) => {
          const radius = Math.min(6 + count * 0.8, 18);

          return (
            <React.Fragment key={idx}>
              {/* Pulsing ring */}
              <CircleMarker
                center={coords}
                radius={radius + 5}
                pathOptions={{
                  color: "#0079C0",
                  weight: 0,
                  fillColor: "#0079C0",
                  fillOpacity: 0.1
                }}
                className="pulse-ring"
              />
              {/* Main marker */}
              <CircleMarker
                center={coords}
                radius={radius}
                pathOptions={{
                  fillColor: "#ffffff",
                  fillOpacity: 0.25,
                  color: "#0079C0",
                  weight: 2,
                  opacity: 0.8
                }}
              >
                <Tooltip
                  direction="top"
                  offset={[0, -10]}
                  opacity={1}
                  permanent={false}
                  className="custom-tooltip"
                >
                  <div style={{
                    backgroundColor: "#F0F8FF",
                    padding: "6px 10px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.15)",
                    color: "#0079C0",
                    fontWeight: "bold",
                    fontSize: "0.85rem",
                    textAlign: "center"
                  }}>
                    {country}: {count} studies
                  </div>
                </Tooltip>
              </CircleMarker>
            </React.Fragment>
          );
        })}
      </MapContainer>
    </div>
  );
}